import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import moment from "moment";

import {
    QUERY_KEY_SUMMARY,
    QUERY_KEY_SUMMARY_FILTERS,
    _api_export_summary,
    _api_get_summary,
    _api_get_summary_filters,
} from "../api/summary";
import { useToastGlobal } from "../../../stores/useToastGlobal";
import { FILTERABLE_STATUSES } from "../components/AnnualSummary/constants";

const toggleValue = (list, value) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const UD_REGEX = /^[ud]-\d+$/;

// filtres lus depuis l'URL (lien partageable), valeurs invalides ignorées
const readFilters = (searchParams) => {
    const year = Number(searchParams.get("year"));
    return {
        year:
            year >= 2000 && year <= 2050
                ? year
                : Number(moment().format("YYYY")),
        ud: searchParams.getAll("ud").filter((v) => UD_REGEX.test(v)),
        status: searchParams
            .getAll("status")
            .filter((v) => FILTERABLE_STATUSES.includes(v)),
    };
};

/**
 * État et données de la synthèse annuelle (Mon équipe / Entreprise)
 * @param {"manager"|"hr"} scope
 */
const useAnnualSummary = (scope) => {
    const setShowToast = useToastGlobal((s) => s.setShow);

    const [searchParams, setSearchParams] = useSearchParams();
    // ud=u-<user_id> / d-<department_id>
    const { year, ud, status } = readFilters(searchParams);

    const setFilters = (next) => {
        const filters = { year, ud, status, ...next };
        const params = new URLSearchParams();
        params.set("year", filters.year);
        filters.ud.forEach((value) => {
            params.append("ud", value);
        });
        filters.status.forEach((value) => {
            params.append("status", value);
        });
        setSearchParams(params, { replace: true });
    };
    const setUd = (fn) => setFilters({ ud: fn(ud) });
    const setStatus = (fn) => setFilters({ status: fn(status) });
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [isExporting, setIsExporting] = useState(false);

    const summary = useQuery(
        [QUERY_KEY_SUMMARY, scope, year, ud, status],
        () => _api_get_summary({ scope, year, ud, status }),
        { keepPreviousData: true, refetchOnWindowFocus: false }
    );

    // les compteurs de la liste État suivent ud (pas status)
    const filters = useQuery(
        [QUERY_KEY_SUMMARY_FILTERS, scope, year, ud],
        () => _api_get_summary_filters({ scope, year, ud }),
        { keepPreviousData: true, refetchOnWindowFocus: false }
    );

    const rows = useMemo(
        () => (summary.data?.groups ?? []).flatMap((group) => group.rows),
        [summary.data]
    );

    const setYear = (value) => {
        if (value === year) return;
        setFilters({ year: value });
        setSelectedUserIds([]);
    };

    // boutons "x mois à déclarer / à valider" : filtre exclusif sur un statut
    const pickStatus = (value) =>
        setStatus((current) =>
            current.length === 1 && current[0] === value ? [] : [value]
        );

    const resetFilters = () => setFilters({ ud: [], status: [] });

    const toggleUser = (user_id) =>
        setSelectedUserIds((current) => toggleValue(current, user_id));

    const isAllSelected =
        rows.length > 0 &&
        rows.every((row) => selectedUserIds.includes(row.user_id));

    const toggleAllUsers = () =>
        setSelectedUserIds(isAllSelected ? [] : rows.map((r) => r.user_id));

    /**
     * Export Excel : la sélection si elle existe, sinon la vue affichée
     * @param {{ start_date: string, end_date: string, onSuccess?: function }} params
     */
    const exportSummary = ({ start_date, end_date, onSuccess }) => {
        setIsExporting(true);
        const params = selectedUserIds.length
            ? { ud: selectedUserIds.map((id) => `u-${id}`), status: [] }
            : { ud, status };

        return _api_export_summary({
            scope,
            start_date,
            end_date,
            ...params,
            cbOnEndDownload: (data, error, message) => {
                setIsExporting(false);
                if (error) {
                    setShowToast({
                        visible: true,
                        level: "danger",
                        content:
                            message ||
                            "Une erreur s'est produite lors de l'export",
                    });
                    return;
                }
                setShowToast({
                    visible: true,
                    level: "success",
                    content: `Export généré · ${message}`,
                    auto_hide_timeout: 4000,
                });
                onSuccess?.();
            },
        });
    };

    return {
        year,
        setYear,
        summary,
        filters,
        rows,
        ud: {
            value: ud,
            toggle: (value) => setUd((current) => toggleValue(current, value)),
            clear: () => setUd(() => []),
        },
        status: {
            value: status,
            toggle: (value) =>
                setStatus((current) => toggleValue(current, value)),
            pick: pickStatus,
        },
        resetFilters,
        selection: {
            value: selectedUserIds,
            toggle: toggleUser,
            toggleAll: toggleAllUsers,
            isAllSelected,
            clear: () => setSelectedUserIds([]),
        },
        exportSummary,
        isExporting,
    };
};

export default useAnnualSummary;
